import { GCInstant as platform } from 'src/lib/gcinstant';
import { EntityManager } from 'src/game/logic/EntityManager';
import { getFriendsPet, getPet } from 'src/game/pet';
import { LivingRoomPosition, Position } from 'src/game/position/PositionModel';
import uiConfig from 'src/lib/ui/config';

const popupWidth = uiConfig.width * 0.8;
export const popup = <const>{
  width: popupWidth,
  height: 200,
  skipTitle: true,
};

export const box = <const>{
  height: popup.height,
  image: `assets/ui/conversation/speechbubble.png`,
  imgScale: 0.66,
  scaleMethod: '9slice',
  sourceSlices: {
    horizontal: { left: 44, right: 44 },
    vertical: { top: 44, bottom: 44 },
  },
};

export const bg = <const>{
  backgroundColor: 'rgba(0,0,0,0)',
};

export const message = <const>{
  x: 33,
  y: 31,
  width: popupWidth - 2 * 33,
  height: popup.height - 31 - 50,
  font: 'Body',
  color: '#91808C',
  size: 32,
  wordWrap: true,
  verticalAlign: 'center',
  align: 'center',
};

export const messageFriend = <const>{
  x: 33,
  y: 31,
  width: popupWidth - 2 * 33,
  height: popup.height - 31 - 50,
  font: 'Body',
  color: '#8eadc9',
  size: 32,
  wordWrap: true,
  verticalAlign: 'center',
  align: 'center',
};

export const tail = <const>{
  image: 'assets/ui/conversation/speechbubble_tail.png',
  y: 181,
  width: 82,
  height: 67,
};

export const tapIcon = <const>{
  image: 'assets/ui/conversation/speechbubble_tap_icon.png',
  x: popupWidth - 50,
  y: popup.height - 45,
  width: 25,
  height: 22,
};

export const descriptive = <const>{
  popup: {
    ...popup,
    height: 303,
  },
  message: {
    ...message,
    height: 40,
  },
  maxLength: {
    x: message.x,
    y: 80,
    size: 25,
    align: 'center',
    width: message.width,
    height: 30,
    font: 'Body',
    color: '#91808C',
    opacity: 0.5,
  },
  input: {
    y: 147,
    x: popupWidth / 2,
    width: popupWidth - 120,
    height: 59,
    centerOnOrigin: true,
    inputStyle: {
      background: '#DAECFA',
      boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '20px',
      color: '#91808C',
      textAlign: 'center',
      fontSize: '10vm',
    },
  },
  button: {
    ...uiConfig.buttons.secondary,
    labelOffsetY: -1,
    fontSize: 31,
    font: 'Title',
    x: popupWidth / 2,
    y: 232,
    width: 228,
    height: 82,
    centerOnOrigin: true,
    tracking: { feature: 'generic', $subFeature: 'popup-input-ok' },
  },
};

export function getTailPositionOpts(
  moveTo: LivingRoomPosition['location'] | undefined,
  playerId: string,
): { x: number; flipX?: boolean } {
  const center = {
    x: popupWidth * 0.51,
  };

  if (!moveTo) {
    // No position is provided so use the pet's current position
    const petPosition =
      platform.playerID === playerId
        ? getPet().position.position
        : getFriendsPet()?.position.position;

    // Pet destroyed, return default.
    if (!petPosition) {
      return center;
    }

    const { scene, location } = petPosition;

    if ((scene === 'livingRoom' || scene === 'friendsLivingRoom') && location) {
      return getTailPositionOpts(petPosition.location, playerId);
    }
  }

  switch (moveTo) {
    case 'tl':
    case 'lm':
    case 'bl': {
      return {
        x: 40,
        flipX: true,
      };
    }

    case 'tr':
    case 'rm':
    case 'br': {
      return {
        x: popupWidth - tail.width - 40,
      };
    }

    case 'tc':
    case 'c':
    case 'bc':
    default: {
      return center;
    }
  }
}

export function getYOffset(moveTo: string) {
  switch (moveTo as Extract<Position, { scene: 'livingRoom' }>['location']) {
    case 'tl':
    case 'tc':
    case 'tr': {
      return -150;
    }

    case 'bl':
    case 'bc':
    case 'br': {
      return -90;
    }

    case 'lm':
    case 'c':
    case 'rm':
    default:
      return -120;
  }
}

export const nameInput = <const>{
  popup: {
    ...popup,
    height: 399,
  },
  message: {
    ...message,
    width: popup.width,
    size: 31,
    height: 40,
    x: 0,
    y: 80,
  },
  header: {
    x: 0,
    y: 15,
    size: 35,
    align: 'center',
    width: popup.width,
    height: 40,
    font: 'Body',
    color: '#6F5166',
  },
  line: {
    y: 60,
    x: (popupWidth - 430) / 2,
    width: 430,
    height: 4,
    backgroundColor: '#DAECFA',
  },
  maxLength: {
    x: message.x,
    y: 136,
    size: 25,
    align: 'center',
    width: message.width,
    height: 30,
    font: 'Body',
    color: '#91808C',
    opacity: 0.5,
  },
  body: {
    x: message.x,
    y: 242,
    size: 20,
    align: 'center',
    width: message.width,
    height: 30,
    font: 'Body',
    color: '#91808C',
    opacity: 0.5,
  },
  input: {
    y: 200,
    x: popupWidth / 2,
    width: popupWidth - 120,
    height: 59,
    centerOnOrigin: true,
    inputStyle: {
      background: '#DAECFA',
      boxShadow: 'inset 0px 4px 4px rgba(0, 0, 0, 0.25)',
      borderRadius: '20px',
      color: '#91808C',
      textAlign: 'center',
      fontSize: '10vm',
    },
  },
  button: {
    ...uiConfig.buttons.secondary,
    labelOffsetY: -1,
    labelOffsetX: -8,
    fontSize: 48,
    font: 'Body',
    x: popupWidth / 2,
    y: 325,
    width: 228,
    height: 82,
    centerOnOrigin: true,
    tracking: { feature: 'generic', $subFeature: 'popup-input-ok' },
  },
};
